/**
 * Portfolio View Component - Spike 13
 *
 * Displays company portfolio with subsidiary hierarchy and products.
 * Shows:
 * - Direct products (not in subsidiaries)
 * - Subsidiaries with their products
 * - Acquisition details and relevance
 */

import { useState } from 'react';
import type { SubsidiaryProfile, Product, MetricSnapshot } from '../../types';

interface PortfolioViewProps {
  companyName: string;
  directProducts: Product[];
  subsidiaries: SubsidiaryProfile[];
  allProducts: Record<string, Product>; // Map of product ID to Product
  onProductSelect: (product: Product) => void;
  isLoading?: boolean;
}

const relevanceColors: Record<string, { bg: string; text: string; label: string }> = {
  core: { bg: 'bg-primary-100', text: 'text-primary-800', label: 'Core' },
  strategic: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Strategic' },
  portfolio: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Portfolio' },
  divesting: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Divesting' },
};

function formatCurrency(value: number): string {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(0)}M`;
  }
  return `$${value.toLocaleString()}`;
}

function MetricDisplay({ metric, label }: { metric?: MetricSnapshot; label: string }) {
  if (!metric) return null;

  const displayValue = typeof metric.value === 'number'
    ? (metric.unit === 'USD' ? formatCurrency(metric.value) : metric.value.toLocaleString())
    : String(metric.value);

  return (
    <div className="text-sm">
      <span className="text-gray-500">{label}:</span>{' '}
      <span className="font-medium text-gray-900">
        {displayValue}
        {metric.unit && metric.unit !== 'USD' && ` ${metric.unit}`}
      </span>
      {metric.is_estimated && (
        <span className="ml-1 text-xs text-amber-600" title="Estimated value">
          (est.)
        </span>
      )}
    </div>
  );
}

function ProductCard({
  product,
  onClick,
}: {
  product: Product;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
    >
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{product.name}</span>
        {product.is_flagship && (
          <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
            Flagship
          </span>
        )}
      </div>
      {product.category && (
        <p className="mt-1 text-sm text-gray-500">{product.category}</p>
      )}
    </button>
  );
}

function SubsidiaryCard({
  subsidiary,
  products,
  onProductSelect,
  defaultExpanded = false,
}: {
  subsidiary: SubsidiaryProfile;
  products: Product[];
  onProductSelect: (product: Product) => void;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Use non-null assertion - we have fallback logic inline
  const relevanceStyle = relevanceColors[subsidiary.relevance] || {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    label: 'Portfolio'
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900">{subsidiary.name}</h4>
              <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${relevanceStyle.bg} ${relevanceStyle.text}`}>
                {relevanceStyle.label}
              </span>
            </div>
            {subsidiary.domain && (
              <p className="text-sm text-gray-500">{subsidiary.domain}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Product count */}
          <span className="text-sm text-gray-500">
            {products.length} product{products.length !== 1 ? 's' : ''}
          </span>

          {/* Expand/collapse */}
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-100">
          {/* Acquisition info */}
          {(subsidiary.acquired_date || subsidiary.acquired_from) && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                {subsidiary.acquired_date && (
                  <span>
                    <span className="text-gray-500">Acquired:</span>{' '}
                    <span className="font-medium">{new Date(subsidiary.acquired_date).getFullYear()}</span>
                  </span>
                )}
                {subsidiary.acquired_from && (
                  <span>
                    <span className="text-gray-500">From:</span>{' '}
                    <span className="font-medium">{subsidiary.acquired_from}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Metrics */}
          {(subsidiary.revenue_contribution || subsidiary.employee_count) && (
            <div className="mb-4 flex flex-wrap gap-4">
              <MetricDisplay metric={subsidiary.revenue_contribution} label="Revenue" />
              <MetricDisplay metric={subsidiary.employee_count} label="Employees" />
            </div>
          )}

          {/* Notes */}
          {subsidiary.notes && (
            <p className="mb-4 text-sm text-gray-600 italic">{subsidiary.notes}</p>
          )}

          {/* Products */}
          {products.length > 0 ? (
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Products</h5>
              <div className="grid gap-2 sm:grid-cols-2">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => onProductSelect(product)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No products mapped to this subsidiary</p>
          )}
        </div>
      )}
    </div>
  );
}

export function PortfolioView({
  companyName,
  directProducts,
  subsidiaries,
  allProducts,
  onProductSelect,
  isLoading,
}: PortfolioViewProps) {
  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-6 w-48 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded-lg"></div>
        <div className="h-32 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  const totalProducts = directProducts.length +
    subsidiaries.reduce((sum, sub) => sum + sub.product_ids.length, 0);

  return (
    <div className="space-y-6" data-testid="portfolio-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{companyName} Portfolio</h3>
          <p className="text-sm text-gray-500">
            {totalProducts} product{totalProducts !== 1 ? 's' : ''} across{' '}
            {subsidiaries.length > 0 ? `${subsidiaries.length + 1} entities` : '1 entity'}
          </p>
        </div>
      </div>

      {/* Direct Products */}
      {directProducts.length > 0 && (
        <section>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
              {directProducts.length}
            </span>
            Direct Products
          </h4>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {directProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => onProductSelect(product)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Subsidiaries */}
      {subsidiaries.length > 0 && (
        <section>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
              {subsidiaries.length}
            </span>
            Subsidiaries
          </h4>
          <div className="space-y-3">
            {subsidiaries.map((subsidiary) => {
              const subProducts = subsidiary.product_ids
                .map((id) => allProducts[id])
                .filter((p): p is Product => p !== undefined);

              return (
                <SubsidiaryCard
                  key={subsidiary.id}
                  subsidiary={subsidiary}
                  products={subProducts}
                  onProductSelect={onProductSelect}
                  defaultExpanded={subsidiary.relevance === 'core'}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Empty state */}
      {directProducts.length === 0 && subsidiaries.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="mt-4 text-gray-500">No portfolio data available</p>
          <p className="text-sm text-gray-400 mt-2">Portfolio information is being collected.</p>
        </div>
      )}
    </div>
  );
}

export default PortfolioView;
